import React, { useCallback, useEffect, useState } from 'react'
import { Button, DataTable, DataTableRow, Row, TextField } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { usePageView } from '@fs/zion-analytics'
import { css } from '@emotion/core'
import { usePageTitle } from '../../app/PageTitleContext'
import { getContactNameSearchResult } from '../../../services/userService'
import { CisLink } from '../../app/Links'
import useNotifications from '../../../hooks/useNotifications'

export default function ContactNameSearch({ history, location }) {
  usePageView({ page_detail: 'DMConsole:ContactNameSearch' })
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.search.contactName'))
  const { error } = useNotifications()
  const [name, setName] = useState('')
  const [searchResults, setSearchResults] = useState()

  const handleContactNameSearch = useCallback(
    (searchName) => {
      setSearchResults()
      if (searchName && searchName !== '') {
        getContactNameSearchResult(searchName)
          .then((response) => {
            // Handle both old format (single object with users) and new format (array of objects with users)
            let allUsers = []
            if (Array.isArray(response.data)) {
              // New format: array of response objects
              allUsers = response.data.flatMap((item) => item.users || [])
            } else if (response.data?.users) {
              // Old format: single object with users array
              allUsers = response.data.users
            }
            setSearchResults(allUsers)
          })
          .catch((err) => {
            error(
              t('search.errors.searchingByContactName', {
                defaultValue: `Error searching by contact name: ${err.message || ''}`,
                message: err.message,
              }),
              err
            )
          })
      }
    },
    [error, t]
  )

  useEffect(() => {
    if (location.search && location.search !== '') {
      const searchParams = new URLSearchParams(location.search)

      const nameParam = searchParams.get('name')
      if (nameParam && nameParam !== '') {
        setName(nameParam)
        handleContactNameSearch(nameParam)
      }
    }
  }, [location.search, handleContactNameSearch])

  const handleFormSubmit = (event) => {
    event.preventDefault()
    const queryString = `name=${name}`

    history.push(`/search/contactname?${queryString}`)
  }

  return (
    <>
      <form onSubmit={(event) => handleFormSubmit(event)}>
        <Row
          css={css`
            display: flex;
            align-items: center;
            gap: 10px;
          `}
        >
          <TextField label={t('name', 'Name')} value={name} onChange={(event) => setName(event.target.value)} />
          <Button type="submit" emphasis="high" disabled={!name || name === ''}>
            {t('action.search', 'Search')}
          </Button>
        </Row>
      </form>
      {searchResults?.length === 0 && <div>{t('search.noResults', 'No results found.')}</div>}
      {searchResults?.length > 0 && (
        <div>
          <div>
            {t('search.contactName.topResultsHelper', 'Showing the top {count} results.', {
              count: searchResults?.length || '0',
            })}
          </div>
          <DataTable
            columns={[
              { label: t('id', 'ID'), size: 'sm' },
              { label: t('contactName', 'Contact Name'), size: 'md' },
              { label: t('userName', 'Username'), size: 'md' },
            ]}
          >
            {searchResults?.map((user, index) => (
              <DataTableRow
                key={user.id || index}
                column1={<UserLink id={user.id} />}
                column2={user.contactName}
                column3={user.username}
              />
            ))}
          </DataTable>
        </div>
      )}
    </>
  )
}

function UserLink({ id }) {
  const arr = id.split('.')
  return <CisLink userId={arr[arr.length - 1]} />
}
